"use client";

import { isIgnoredBrowserNotice, isIgnoredExtensionError } from "@/lib/diagnostics/ignored-browser-notices";
import { recordDiagnosticsErrorFromClient, recordDiagnosticsFromClient } from "@/lib/diagnostics/record-client";
import { useEffect } from "react";

/**
 * Dev-only global error capture. Mounted from ConvexClientProvider so CRM,
 * portal, and dispute-round surfaces all feed the diagnostics bus.
 *
 * Capture only. Pulling server-recorded events back into the store is
 * `useServerDiagnosticsPoll` in `diagnostics-store.ts`, which runs only while
 * the diagnostics panel is open.
 */
export function DiagnosticsBootstrap() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    const handleError = (event: ErrorEvent) => {
      // Browser notices arrive on this listener as ErrorEvents with no `error`,
      // no stack, and no source position. Nothing threw and nothing is broken,
      // so they never enter the bus (see ignored-browser-notices.ts).
      if (isIgnoredBrowserNotice(event.message) || isIgnoredExtensionError(event.filename)) {
        return;
      }

      recordDiagnosticsFromClient({
        source: "client",
        message: event.message || "Unhandled error",
        stack: event.error instanceof Error ? event.error.stack ?? null : null,
        code: null,
        context: {
          filename: event.filename || null,
          lineno: event.lineno || null,
          colno: event.colno || null,
        },
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      recordDiagnosticsErrorFromClient("client", event.reason, {
        context: { kind: "unhandled_rejection" },
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
